import {useContext, useEffect, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {css} from '@emotion/css';
import {parsePhoneNumberFromString} from 'libphonenumber-js';
import {AxiosError} from 'axios';
import {sizes} from 'patient-ping-remedy/packages/theme';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import Checkbox from 'patient-ping-remedy/packages/checkbox';
import Modal from 'patient-ping-remedy/packages/modal';
import Spacer from 'patient-ping-remedy/packages/spacer';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useActiveRoster} from '@/store/roster_store';
import {useAlertStore} from '@/store/alert_store';
import {ConnectContactAssociateResult, ConnectContactResponse} from '@/api/dto/connect';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import {createFormFieldConfig, useForm} from '@/hooks/useForm';
import Validate from '@/helpers/validate';
import DisplayHelpers from '@/helpers/display_helpers';
import Helpers from '@/helpers/helpers';
import ConfirmModal from '@common/ConfirmModal';
import ClientDropdownList from '@clients/referral/ClientDropdownList';
import AwsCommunicationsContact from '@/components/pages/tasks/aws_communications/contact/AwsCommunicationsContact';
import {AssociationOptionHint, AssociationOptionLabel, AssociationOptions, Content, Separator}
  from '@/components/pages/tasks/aws_communications/AwsCommunicationAssociateModal.styles';
import {log} from '@/helpers/logger';

const associateContactResults: Record<ConnectContactAssociateResult, string> = {
  [ConnectContactAssociateResult.COMPLETE]: 'Contact associated successfully.',
  [ConnectContactAssociateResult.COMPLETE_BUT_CODE_BINDING_MISSING]:
    "Contact associated successfully, but code binding is missing to add it to client's additional details.",
  [ConnectContactAssociateResult.COMPLETE_BUT_PHONE_FIELDS_SLOTS_MISSING]:
    "Contact associated successfully, but there're no more left field slots to add it to client's additional details.",
  [ConnectContactAssociateResult.FAILED]: 'Failed to associate contact.',
};

type AssociateContactParams = {
  clientId: string;
  associateAllFromNumber: boolean;
  addNumberPermanently: boolean;
}

type Props = {
  isOpen: boolean;
  close(): void;
  connectContact: ConnectContactResponse;
  rosterId?: number;
}

export default function AwsCommunicationAssociateModal({ isOpen, close, connectContact, rosterId }: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const activeRoster = useActiveRoster();
  const { addAlert } = useAlertStore();
  const queryClient = useQueryClient();

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  // Prefer the contact's own roster, then the roster resolved by the drawer (the "assumed" roster on
  // the cross-roster Tasks page where useActiveRoster() is null -- see CARECO-5939), then the active
  // roster. This roster both authorizes the associate call and scopes the client search below, so a
  // navigator can only pick a client within the assumed roster (the backend then binds the contact to
  // that client's roster). Multi-group association across other rosters remains CARECO-5702.
  const effectiveRosterId = connectContact.rosterId ?? rosterId ?? activeRoster?.id;

  const { mutateAsync: associateContact, isPending } = useMutation({
    mutationFn: async ({ clientId, associateAllFromNumber, addNumberPermanently }: AssociateContactParams) =>
      carecoApi?.associateContact(connectContact.id, effectiveRosterId!, clientId, associateAllFromNumber, addNumberPermanently),
    onSuccess(response) {
      addAlert({
        type: ToastType.SUCCESS,
        content: associateContactResults[response?.result ?? ConnectContactAssociateResult.COMPLETE],
      });
      void queryClient.invalidateQueries({ queryKey: ['connect', 'contacts'] });
    },
    onError(error: AxiosError) {
      Helpers.alertOnError(associateContactResults[ConnectContactAssociateResult.FAILED]
        + ` ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const { form, isValid, isFormChanged, setInitialForm, handleChange, handleSubmit } = useForm(async () => {
    try {
      await associateContact({
        clientId: form.client?.value?.value,
        associateAllFromNumber: form.associate_all_from_number?.value,
        addNumberPermanently: form.add_number_permanently?.value,
      });
      close();
    } catch (error) {
      log('error', error);
    }
  });

  useEffect(() => {
    if (isOpen) {
      setInitialForm({
        ...createFormFieldConfig(
          'client',
          null,
          { valid: false, message: '' },
          [Validate.required],
          true,
        ),
        ...createFormFieldConfig(
          'associate_all_from_number',
          false,
          { valid: true, message: '' },
          [],
          false,
        ),
        ...createFormFieldConfig(
          'add_number_permanently',
          false,
          { valid: true, message: '' },
          [],
          false,
        ),
      });
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isFormChanged() && !isPending) {
      setConfirmModalOpen(true);
    } else {
      close();
    }
  };

  const handleLeave = () => {
    setConfirmModalOpen(false);
    close();
  };

  const nationalNumber = parsePhoneNumberFromString(connectContact.phoneNumber)?.nationalNumber;
  const phoneNumber = DisplayHelpers.formatPhoneNumber(nationalNumber);

  return (
    <>
      <Modal
        isOpen={isOpen}
        headerText="Associate contact"
        className={css({ width: '70rem' })}
        buttons={[
          <Button key="cancel" styleType={StyleType.TERTIARY} onClick={handleClose}>Cancel</Button>,
          <Button
            key="associate"
            disabled={!isValid() || isPending || !effectiveRosterId}
            onClick={handleSubmit}
          >
            Associate
          </Button>
        ]}
      >
        <Content>
          <AwsCommunicationsContact connectContact={connectContact} rosterId={effectiveRosterId} hideConversationLink />
          <Spacer itemHeight={sizes.medium} />
          <ClientDropdownList form={form} handleChange={handleChange} rosterId={effectiveRosterId} isWithinModal />
          {!!form.client?.value && (
            <>
              <Separator />
              <AssociationOptions>
                <Checkbox
                  value={form.add_number_permanently?.value}
                  onChange={(value, e) => handleChange(e, 'add_number_permanently', value)}
                  label={(
                    <AssociationOptionLabel>
                      Add this number to client's additional details
                      <AssociationOptionHint>
                        Future calls from {phoneNumber} will automatically match to {form.client?.value.label}
                      </AssociationOptionHint>
                    </AssociationOptionLabel>
                  ) as unknown as string}
                />
                <Checkbox
                  value={form.associate_all_from_number?.value}
                  onChange={(value, e) => handleChange(e, 'associate_all_from_number', value)}
                  label={(
                    <AssociationOptionLabel>
                      Associate all contacts from {phoneNumber}
                      <AssociationOptionHint>
                        All unassociated contacts from this number will be linked
                      </AssociationOptionHint>
                    </AssociationOptionLabel>
                  ) as unknown as string}
                />
              </AssociationOptions>
            </>
          )}
        </Content>
      </Modal>
      <ConfirmModal
        show={confirmModalOpen}
        showFunction={setConfirmModalOpen}
        leaveFunction={handleLeave}
      />
    </>
  );
}
